import { z } from 'zod';
import { PlanCode } from './plan';

export const AdminRefundPreviewRequest = z
  .object({
    amount_cents: z.number().int().positive().max(100_000_000),
  })
  .strict();
export type AdminRefundPreviewRequest = z.infer<typeof AdminRefundPreviewRequest>;

export const AdminRefundCommitRequest = AdminRefundPreviewRequest.extend({
  state_token: z.string().min(16).max(128),
  idempotency_key: z.string().uuid(),
  reason: z.string().trim().min(1).max(500),
  acknowledge_entitlement_risk: z.boolean().default(false),
}).strict();
export type AdminRefundCommitRequest = z.infer<typeof AdminRefundCommitRequest>;

const AdminRefundUsage = z.object({
  quota_key: z.string(),
  used: z.number().int().nonnegative(),
  before_limit: z.number().int(),
  current_limit: z.number().int(),
});

export const AdminRefundPreviewResponse = z.object({
  state_token: z.string().min(16).max(128),
  tenant_id: z.string(),
  payment_id: z.string(),
  invoice_id: z.string(),
  original_amount_cents: z.number().int().nonnegative(),
  refunded_amount_cents: z.number().int().nonnegative(),
  refundable_amount_cents: z.number().int().nonnegative(),
  requested_amount_cents: z.number().int().positive(),
  full_refund: z.boolean(),
  current_plan_code: PlanCode,
  resulting_plan_code: PlanCode,
  entitlement_will_change: z.boolean(),
  risk: z.object({
    requires_acknowledgement: z.boolean(),
    quota_overages: z.array(AdminRefundUsage),
    storage_used_bytes: z.number().int().nonnegative().nullable(),
    resulting_storage_limit_bytes: z.number().int().nullable(),
  }),
});
export type AdminRefundPreviewResponse = z.infer<typeof AdminRefundPreviewResponse>;

export const AdminRefundCommitResponse = z.object({
  refund: z.object({
    id: z.string(),
    tenant_id: z.string(),
    payment_id: z.string(),
    invoice_id: z.string(),
    amount_cents: z.number().int().positive(),
    cumulative_refunded_cents: z.number().int().positive(),
    full_refund: z.boolean(),
    resulting_plan_code: PlanCode,
    created_at: z.string(),
    replayed: z.boolean(),
  }),
});
export type AdminRefundCommitResponse = z.infer<typeof AdminRefundCommitResponse>;

export const PaymentRiskEventRequest = z
  .object({
    event_id: z.string().min(1).max(200),
    event: z.enum(['chargeback', 'payment_reversed']),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();
export type PaymentRiskEventRequest = z.infer<typeof PaymentRiskEventRequest>;
